import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import tumbado from './images/tumbado.png';

interface UploadedFile {
    file: File;
    preview: string;
}

const ContentCreationScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [interpretCopy, setInterpretCopy] = useState(true); // Nuevo estado para el checkbox
    const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isExiting, setIsExiting] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [savingDots, setSavingDots] = useState('');
    const [titleError, setTitleError] = useState<string | null>(null);
    const [existingTitles, setExistingTitles] = useState<string[]>([]);
    
    // Estados para la pantalla de carga
    const [showLoadingScreen, setShowLoadingScreen] = useState(false);

    // Obtener la fecha de publicación desde location.state
    const postDate = location.state?.postDate;
    const originYear = location.state?.originYear;
    const originMonth = location.state?.originMonth;
    const adminViewUserId = location.state?.adminViewUserId; // Para admin creando posts para otros usuarios
    const postId = location.state?.postId;
    const isEdit = !!postId;

    // Cargar títulos existentes del usuario
    useEffect(() => {
        const fetchExistingTitles = async () => {
            try {
                const token = localStorage.getItem('token');
                const url = adminViewUserId 
                    ? `${process.env.REACT_APP_API_URL}/posts/titles?userId=${adminViewUserId}`
                    : `${process.env.REACT_APP_API_URL}/posts/titles`;
                
                const res = await fetch(url, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                
                if (res.ok) {
                    const titles = await res.json();
                    setExistingTitles(titles);
                }
            } catch (err) {
                console.error('Error al cargar títulos existentes:', err);
            }
        };
        
        fetchExistingTitles();
    }, [adminViewUserId]);

    // Cargar datos del post si es edición
    useEffect(() => {
        if (isEdit) {
            const fetchPost = async () => {
                try {
                    const token = localStorage.getItem('token');
                    const res = await fetch(`${process.env.REACT_APP_API_URL}/posts/${postId}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (res.ok) {
                        const post = await res.json();
                        setTitle(post.title);
                        setDescription(post.description);
                        setInterpretCopy(post.interpretCopy !== undefined ? post.interpretCopy : true);
                        setUploadedFiles(post.images && post.images.length > 0
                            ? [{ 
                                file: new File([], 'imagen'), 
                                preview: post.images[0].startsWith('/uploads/post-images/') 
                                    ? `${process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:3001'}${post.images[0]}`
                                    : post.images[0]
                              }]
                            : []);
                        // Si hay postDate, actualiza el estado si lo usas como state
                        // setPostDate(post.postDate) si usas un useState para postDate
                    } else {
                        setError('No se pudo cargar el post para editar.');
                    }
                } catch (err) {
                    setError('Error al cargar el post para editar.');
                }
            };
            fetchPost();
        }
    }, [isEdit, postId]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoaded(true);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    // Animación de puntos suspensivos durante el guardado
    useEffect(() => {
        if (isSaving) {
            const interval = setInterval(() => {
                setSavingDots(prev => {
                    if (prev === '') return '.';
                    if (prev === '.') return '..';
                    if (prev === '..') return '...';
                    return '';
                });
            }, 500); // Cambiar cada 500ms
            
            return () => clearInterval(interval);
        } else {
            setSavingDots('');
        }
    }, [isSaving]);

    const handleLogout = () => {
        setIsExiting(true);
        setTimeout(() => {
            navigate('/thank-you');
        }, 500);
    };

    const handleGoToCalendar = () => {
        setIsExiting(true);
        setTimeout(() => {
            if (originYear !== undefined && originMonth !== undefined) {
                const calendarState: any = { 
                    year: originYear, 
                    month: originMonth,
                    fromContentCreation: true // Flag para indicar que viene de creación de contenido
                };
                // Si es admin viendo otro usuario, incluir la información del usuario
                if (adminViewUserId) {
                    calendarState.adminViewUserId = adminViewUserId;
                    calendarState.adminViewUserEmail = location.state?.adminViewUserEmail;
                    calendarState.adminViewUserPlan = location.state?.adminViewUserPlan;
                }
                navigate('/calendar', { state: calendarState });
            } else {
                navigate('/calendar', { state: { fromContentCreation: true } });
            }
        }, 500);
    };

    const handleFiles = (newFiles: FileList) => {
        const imageFiles = Array.from(newFiles).filter(file => file.type.startsWith('image/'));
        if (imageFiles.length > 0) {
            const file = imageFiles[0];
            
            // Validar tamaño del archivo (20MB = 20 * 1024 * 1024 bytes)
            const maxSize = 20 * 1024 * 1024; // 20MB
            if (file.size > maxSize) {
                setError(`La imagen es demasiado grande (${(file.size / 1024 / 1024).toFixed(2)}MB). El tamaño máximo permitido es 20MB.`);
                return;
            }
            
            // Limpiar error anterior si existe
            setError(null);
            
            const reader = new FileReader();
            reader.onloadend = () => {
                setUploadedFiles([{ file, preview: reader.result as string }]);
                setImageLoaded(false);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFiles(e.dataTransfer.files);
            e.dataTransfer.clearData();
        }
    };

    const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            handleFiles(e.target.files);
        }
    };

    const handleSelectFilesClick = () => {
        fileInputRef.current?.click();
    };

    const handleRemoveFile = (index: number) => {
        setUploadedFiles(prev => prev.filter((_, i) => i !== index));
    };

    const isSaveButtonActive = title && description && postDate;

    // Función para validar si el título ya existe
    const validateTitle = (titleToValidate: string): boolean => {
        if (isEdit) {
            // En modo edición, no validar si es el mismo título del post actual
            return true;
        }
        
        const titleExists = existingTitles.some(existingTitle => 
            existingTitle.toLowerCase().trim() === titleToValidate.toLowerCase().trim()
        );
        
        if (titleExists) {
            setTitleError('Ya has usado ese título. Por favor, escoge otro');
            return false;
        } else {
            setTitleError(null);
            return true;
        }
    };



    const handleSave = async () => {
        if (isSaveButtonActive) {
            // Validar título antes de proceder
            if (!validateTitle(title)) {
                return; // No proceder si el título ya existe
            }
            
            // Mostrar pantalla de carga inmediatamente
            setShowLoadingScreen(true);
            setError(null);
            
            try {
                const token = localStorage.getItem('token');
                const images = uploadedFiles.length > 0 ? uploadedFiles.map(f => f.preview) : [];
                const postData: any = {
                    title,
                    description,
                    images,
                    postDate,
                    interpretCopy
                };
                if (adminViewUserId) {
                    postData.userId = adminViewUserId;
                }

                if (isEdit) {
                    // PATCH para editar
                    const res = await fetch(`${process.env.REACT_APP_API_URL}/posts/${postId}`, {
                        method: 'PATCH',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(postData)
                    });
                    if (res.ok) {

                        
                        const date = new Date(postDate);
                        setShowLoadingScreen(false);
                        setIsExiting(true);
                        setTimeout(() => {
                            const calendarState: any = { 
                                year: date.getFullYear(), 
                                month: date.getMonth(),
                                fromContentCreation: true
                            };
                            if (adminViewUserId) {
                                calendarState.adminViewUserId = adminViewUserId;
                                calendarState.adminViewUserEmail = location.state?.adminViewUserEmail;
                                calendarState.adminViewUserPlan = location.state?.adminViewUserPlan;
                            }
                            navigate('/calendar', { state: calendarState });
                        }, 500);
                    } else {
                        const errorData = await res.json().catch(() => ({}));
                        setError(errorData.details || 'Error al guardar los cambios.');
                        setShowLoadingScreen(false);
                    }
                } else {
                    // POST para crear
                    const res = await fetch(`${process.env.REACT_APP_API_URL}/posts`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(postData)
                    });
                    if (res.ok) {

                        
                        const date = new Date(postDate);
                        setShowLoadingScreen(false);
                        setIsExiting(true);
                        setTimeout(() => {
                            const calendarState: any = { 
                                year: date.getFullYear(), 
                                month: date.getMonth(),
                                fromContentCreation: true
                            };
                            if (adminViewUserId) {
                                calendarState.adminViewUserId = adminViewUserId;
                                calendarState.adminViewUserEmail = location.state?.adminViewUserEmail;
                                calendarState.adminViewUserPlan = location.state?.adminViewUserPlan;
                            }
                            navigate('/calendar', { state: calendarState });
                        }, 500);
                    } else {
                        const errorData = await res.json().catch(() => ({}));
                        setError(errorData.details || 'Error al guardar la publicación. Inténtalo nuevamente.');
                        setShowLoadingScreen(false);
                    }
                }
            } catch (err) {
                setError('Error de conexión. Verifica tu conexión a internet e inténtalo nuevamente.');
                setShowLoadingScreen(false);
            } finally {
                setIsSaving(false);
            }
        }
    };

    return (
        <>
            {/* Pantalla de carga con progress bar */}
            {showLoadingScreen && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-200 bg-opacity-80 transition-all duration-500 ease-out"
                    style={{ pointerEvents: 'all' }}
                >
                    <div className="flex flex-col items-center animate-fadeIn">
                        {/* Spinner principal */}
                        <div className="flex flex-col items-center mb-6">
                            <svg className="animate-spin h-16 w-16 text-[#44aaf6] mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                        
                        {/* Mensaje simple */}
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-[#44aaf6] mb-2 tracking-wide">
                                Guardando publicación...
                            </h2>
                            <p className="text-gray-600 text-lg">
                                Por favor espera un momento
                            </p>
                        </div>
                    </div>
                </div>
            )}
            
            {/* VERSIÓN MÓVIL */}
            <div
                className={`block sm:hidden bg-[#F8F9FA] min-h-screen w-full font-futura px-0 pb-8 relative overflow-y-auto transition-all duration-700 ease-in-out ${isExiting ? 'opacity-0' : isLoaded ? 'opacity-100' : 'opacity-0'}`}
                style={{ pointerEvents: isLoaded && !isExiting ? 'auto' : 'none' }}
            >
                {/* Estilos CSS específicos para móvil - asegurar scroll */}
                <style dangerouslySetInnerHTML={{
                    __html: `
                        @media (max-width: 640px) {
                            html, body { 
                                overflow: auto !important; 
                                overflow-x: hidden !important;
                                height: auto !important;
                                min-height: 100vh !important;
                            }
                            #root { 
                                overflow: visible !important; 
                                height: auto !important;
                            }
                        }
                    `
                }} />
                {/* Botón Cerrar sesión */}
                <div className="flex justify-end px-4 pt-6">
                    <button
                        onClick={handleLogout}
                        className="px-4 py-1.5 rounded-full bg-white shadow text-[#44aaf6] font-bold text-sm border border-white hover:bg-blue-50 transition-all"
                        style={{ letterSpacing: '0.08em' }}
                    >
                        Cerrar sesión
                    </button>
                </div>
                <div className="flex flex-col items-start w-full px-6 pt-2 overflow-y-auto" style={{ minHeight: 'calc(100vh - 80px)' }}>
                    <h1 className="text-3xl font-bold text-[#5AB2F7] leading-tight tracking-wide mb-2 text-left" style={{ letterSpacing: '0.06em' }}>
                        {isEdit ? 'Editar publicación' : 'Ayúdanos a crear'}<br />{isEdit ? '' : 'tu contenido'}
                    </h1>
                    <h2 className="text-lg font-bold text-black mb-2 text-left">{isEdit ? 'Modifica los datos de tu publicación' : 'Cuéntanos un poco de que se trata tu publicación'}</h2>
                    <p className="text-gray-600 mb-2 text-sm text-left">
                        En este espacio puedes contarnos qué te gustaría comunicar esta semana en tus redes sociales.
                    </p>
                    <p className="text-gray-600 text-sm text-left mb-4">
                        Escríbenos un mensaje breve con ideas, promociones, noticias o cualquier detalle importante que quieras compartir. Nuestro equipo usará esta información como base para crear una publicación alineada con tu marca.
                    </p>
                    <img src={tumbado} alt="Persona creando contenido" className="w-64 mb-4 self-center" />
                    {/* Title Input */}
                    <div className="w-full mb-4">
                        <label className="text-sm font-semibold text-gray-700 mb-1 block text-left">Escribe un título orientativo</label>
                        <input
                            type="text"
                            placeholder="Ej: Ofertas de primavera"
                            value={title}
                            onChange={(e) => { 
                                if (e.target.value.length <= 100) {
                                    setTitle(e.target.value);
                                    // Limpiar error cuando el usuario empiece a escribir
                                    if (titleError) setTitleError(null);
                                }
                            }}
                            maxLength={100}
                            className={`w-full p-3 rounded-2xl placeholder-gray-400 text-gray-800 outline-none border-none ${
                                titleError ? 'bg-red-50 border-2 border-red-200' : 'bg-[#eaf4fd]'
                            }`}
                        />
                        {titleError && (
                            <p className="text-red-500 text-sm mt-1 text-left">{titleError}</p>
                        )}
                    </div>
                    {/* Description Input */}
                    <div className="w-full mb-4">
                        <label className="text-sm font-semibold text-gray-700 mb-1 block text-left">Cuéntanos un poco de se trata tu publicación</label>
                        <textarea
                            placeholder="Ej: Queremos anunciar nuestras ofertas especiales por la llegada de la primavera. Incluir que habrá descuentos de hasta el 30% en productos seleccionados."
                            value={description}
                            onChange={(e) => { if (e.target.value.length <= 2201) setDescription(e.target.value); }}
                            maxLength={2201}
                            className="w-full p-3 rounded-2xl bg-[#eaf4fd] placeholder-gray-400 text-gray-800 outline-none border-none min-h-[140px]"
                        />
                    </div>
                    
                    {/* Interpret Copy Checkbox */}
                    <div className="w-full mb-4 flex justify-center -mt-2">
                        <label className="flex items-center gap-2 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={interpretCopy}
                                onChange={(e) => setInterpretCopy(e.target.checked)}
                                className="hidden peer"
                            />
                            <span
                                className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white"
                                style={{ minWidth: 24, minHeight: 24 }}
                            >
                                {interpretCopy && (
                                    <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </span>
                            <span className="text-sm font-semibold text-gray-700">
                                Enriquecer texto
                            </span>
                        </label>
                    </div>
                    {/* File Upload */}
                    <div className="w-full mb-6">
                        <label className="text-sm font-semibold text-gray-700 mb-1 block text-left">Sube tus fotos o videos</label>
                        {uploadedFiles.length === 0 ? (
                            <div
                                className={`w-full rounded-2xl border-2 border-dashed ${isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-[#f8fafd]'} flex flex-col items-center justify-center py-6 cursor-pointer transition-all`}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onClick={handleSelectFilesClick}
                            >
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={fileInputRef}
                                    className="hidden"
                                    onChange={handleFileInputChange}
                                    multiple={false}
                                />
                                <span className="text-[#44aaf6] font-semibold text-base mb-2">Seleccionar archivos</span>
                            </div>
                        ) : (
                            <div className="flex flex-col items-center justify-center w-full h-full relative mt-3">
                                {/* Solo mostrar la primera imagen */}
                                <img src={uploadedFiles[0].preview} alt="preview" className="max-w-[220px] max-h-[220px] rounded-2xl shadow-md object-cover mx-auto transition-opacity duration-500" />
                                <button
                                    className="absolute top-2 right-2 bg-white bg-opacity-80 rounded-full p-1 text-gray-500 hover:text-red-500"
                                    onClick={(e) => { e.stopPropagation(); handleRemoveFile(0); }}
                                >&#10005;</button>
                            </div>
                        )}
                    </div>
                    {/* Mensaje de error */}
                    {error && (
                        <div className="w-full mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-red-600 text-sm">{error}</p>
                        </div>
                    )}
                    
                    {/* Guardar botón */}
                    <button
                        onClick={handleSave}
                        disabled={!isSaveButtonActive || isSaving}
                        className={`w-full py-4 rounded-full font-bold text-white text-lg shadow transition-all ${
                            isSaveButtonActive && !isSaving 
                                ? 'bg-[#44aaf6] hover:bg-blue-500' 
                                : 'bg-blue-200 cursor-not-allowed'
                        }`}
                    >
                        {isSaving ? `Guardando${savingDots}` : isEdit ? 'Guardar cambios' : 'Guardar publicación'}
                    </button>
                </div>
            </div>

            {/* VERSIÓN ESCRITORIO */}
            <div className="hidden sm:block">
                <div
                    className="bg-[#F8F9FA] min-h-screen w-full font-futura px-4 sm:px-8 lg:px-16 relative flex items-center transition-opacity duration-500 ease-in-out"
                    style={{
                        opacity: isExiting ? 0 : isLoaded ? 1 : 0,
                        overflow: 'hidden'
                    }}
                >
                    {/* Estilos CSS para ocultar scrollbar SOLO en escritorio */}
                    <style dangerouslySetInnerHTML={{
                        __html: `
                            body { overflow: hidden !important; }
                            ::-webkit-scrollbar { display: none; }
                            -ms-overflow-style: none;
                            scrollbar-width: none;
                        `
                    }} />
                    {/* Botón Cerrar sesión */}
                    <div className="absolute top-6 right-10 z-30">
                        <button
                            onClick={handleLogout}
                            className="px-8 py-3 rounded-full bg-white shadow text-[#44aaf6] font-bold tracking-wide text-lg border border-white hover:bg-blue-50 transition-all"
                            style={{ letterSpacing: '0.08em' }}
                        >
                            Cerrar sesión
                        </button>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-16 max-w-screen-xl mx-auto items-start">
                        {/* Left Column */}
                        <div className="flex flex-col justify-center">
                            <h1 className="text-5xl xl:text-6xl font-bold text-[#5AB2F7] leading-tight tracking-wide mb-6" style={{ letterSpacing: '0.06em' }}>
                                {isEdit ? 'Editar publicación' : 'Ayúdanos a crear'}<br />{isEdit ? '' : 'tu contenido'}
                            </h1>
                            <h2 className="text-3xl font-bold text-black mb-4">{isEdit ? 'Modifica los datos de tu publicación' : 'Cuéntanos un poco de que se trata tu publicación'}</h2>
                            <p className="text-gray-600 mb-4 text-base">
                                En este espacio puedes contarnos qué te gustaría comunicar esta semana en tus redes sociales.
                            </p>
                            <p className="text-gray-600 text-base">
                                Escríbenos un mensaje breve con ideas, promociones, noticias o cualquier detalle importante que quieras compartir. Nuestro equipo usará esta información como base para crear una publicación alineada con tu marca.
                            </p>
                            <div className="mt-8">
                                <img src={tumbado} alt="Persona creando contenido" className="w-full max-w-lg mx-auto" />
                            </div>
                        </div>

                        {/* Right Column */}
                        <div className="w-full flex flex-col">
                            <div className="space-y-8">
                                {/* Title Input */}
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-sm font-semibold text-gray-700">Escribe un título orientativo</label>
                                        <button 
                                            onClick={handleGoToCalendar} 
                                            className="text-gray-400 hover:text-gray-600 text-2xl"
                                        >
                                            &#x2715;
                                        </button>
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Ej: Ofertas de primavera"
                                        value={title}
                                        onChange={(e) => {
                                            if (e.target.value.length <= 100) {
                                                setTitle(e.target.value);
                                                // Limpiar error cuando el usuario empiece a escribir
                                                if (titleError) setTitleError(null);
                                            }
                                        }}
                                        maxLength={100}
                                        className={`w-full p-4 rounded-full border-2 focus:ring-0 outline-none placeholder-gray-400 text-gray-800 ${
                                            titleError 
                                                ? 'bg-red-50 border-red-200 focus:border-red-300' 
                                                : 'bg-[#e8edf4] border-transparent focus:border-blue-300'
                                        }`}
                                    />
                                    {titleError && (
                                        <p className="text-red-500 text-sm mt-1">{titleError}</p>
                                    )}
                                </div>

                                {/* Description Textarea */}
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 mb-2 block">Cuéntanos un poco de qué se trata tu publicación</label>
                                    <textarea
                                        placeholder="Ej: Queremos anunciar nuestras ofertas especiales por la llegada de la primavera. Incluir que habrá descuentos de hasta el 30% en productos seleccionados."
                                        value={description}
                                        onChange={(e) => {
                                            if (e.target.value.length <= 2201) setDescription(e.target.value);
                                        }}
                                        maxLength={2201}
                                        className="w-full p-4 rounded-[2rem] bg-[#e8edf4] border-2 border-transparent focus:border-blue-300 focus:ring-0 outline-none h-40 -mb-4 resize-none placeholder-gray-400 text-gray-800 leading-relaxed"
                                    />
                                </div>
                                
                                {/* Interpret Copy Checkbox */}
                                <div className="flex justify-center -mt-2">
                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                        <input
                                            type="checkbox"
                                            checked={interpretCopy}
                                            onChange={(e) => setInterpretCopy(e.target.checked)}
                                            className="hidden peer"
                                        />
                                        <span
                                            className="w-6 h-6 flex items-center justify-center rounded-lg border-2 border-gray-300 bg-white transition-all duration-150 peer-checked:border-[#44aaf6] peer-checked:bg-white"
                                            style={{ minWidth: 24, minHeight: 24 }}
                                        >
                                            {interpretCopy && (
                                                <svg className="w-4 h-4 text-[#44aaf6]" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </span>
                                        <span className="text-sm font-semibold text-gray-700">
                                            Enriquecer texto
                                        </span>
                                    </label>
                                </div>

                                {/* File Upload */}
                                <div>
                                    <label className="text-sm font-semibold text-gray-700 mb-2 block">Sube tu imagen</label>
                                    <input
                                        type="file"
                                        multiple={false}
                                        ref={fileInputRef}
                                        onChange={handleFileInputChange}
                                        className="hidden"
                                        accept="image/*"
                                    />
                                    <div
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        className={`border-2 border-dashed border-gray-300 rounded-[3rem] p-8 text-center transition-colors flex items-center justify-center min-h-[200px] relative ${isDragging ? 'bg-blue-50' : ''}`}
                                    >
                                        {uploadedFiles.length === 0 ? (
                                            <div className="w-full">
                                                <p className="text-gray-500 mb-2">Arrastra y suelta para subir tu imagen</p>
                                                <p className="text-gray-500 mb-4">O</p>
                                                <button
                                                    onClick={handleSelectFilesClick}
                                                    className="px-6 py-3 rounded-full bg-[#EFF6FF] text-[#44AAF6] font-semibold hover:bg-blue-100 transition-all"
                                                >
                                                    Seleccionar imagen
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col items-center justify-center w-full h-full relative">
                                                <img
                                                    src={uploadedFiles[0].preview}
                                                    alt={uploadedFiles[0].file.name}
                                                    className="max-w-[180px] max-h-[180px] rounded-2xl shadow-md object-cover mx-auto transition-opacity duration-500"
                                                    style={{ opacity: imageLoaded ? 1 : 0 }}
                                                    onLoad={() => setImageLoaded(true)}
                                                />
                                                <button
                                                    onClick={() => handleRemoveFile(0)}
                                                    className="absolute top-2 right-2 bg-white bg-opacity-80 rounded-full p-1 text-gray-500 hover:text-black hover:bg-opacity-100 transition"
                                                    aria-label="Eliminar imagen"
                                                    style={{ zIndex: 2 }}
                                                >
                                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            {/* Mensaje de error */}
                            {error && (
                                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-red-600 text-sm">{error}</p>
                                </div>
                            )}
                            
                            {/* Save Button */}
                            <div className="mt-auto pt-8 flex justify-end">
                                <button
                                    disabled={!isSaveButtonActive || isSaving}
                                    onClick={handleSave}
                                    className={`px-10 py-3 rounded-full border-2 text-lg font-semibold transition-all ${
                                        isSaveButtonActive && !isSaving
                                        ? 'border-[#44aaf6] text-[#44aaf6] hover:bg-blue-50'
                                        : 'border-gray-300 text-gray-400 cursor-not-allowed bg-gray-100'
                                    }`}
                                >
                                    {isSaving ? `Guardando${savingDots}` : isEdit ? 'Guardar cambios' : 'Guardar publicación'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ContentCreationScreen; 